/**
 * boardOptimize (OPTIMIZE-DAY-1) — whole-board route optimizer for one day.
 *
 * Two moves, layered:
 *   1. RE-SEQUENCE every tech's run in window-aware order (the same banded 2-opt
 *      the per-tech editor uses) — pure win, no reassignment.
 *   2. RELOCATE *movable* calls to a closer, QUALIFIED tech when it shortens the
 *      day overall — greedy best-insertion local search.
 *
 * Hard rules (Seth, 2026-06-24): a PART call, a TECH (tech-specific) call, a PAIR
 * (2-tech) call, a return visit, an INSTALL, or any call the customer requested a
 * specific tech for is ANCHORED — it may be re-ordered within its OWN tech's day
 * but is NEVER handed to another tech here (that only happens when its tech is OUT,
 * via Rebuild Day). Only plain windowed service calls, LONG, and QUOTE float.
 * A relocate also respects the call's required skills and never lands on a tech the
 * notes told us to avoid. Everything is a PROPOSAL — the caller reviews + commits.
 *
 * Pure + deterministic (clones its inputs) so it's unit-testable and never mutates
 * the board's live data.
 */
import { routeMiles, optimizeBanded, driveMinutes, type LatLng } from "./route";

export interface OptiStop {
  id: string;
  empno: string; // current assigned tech
  lat: number;
  lng: number;
  band: number; // time band (window) for the banded optimizer
  t: string; // "HH:MM" planned-time sort key (for the honest "today" baseline)
  movable: boolean; // derived lock: false = anchored to its tech
  requiredSkills: number[]; // skill_domain ids this call needs
  excludedEmpnos: string[]; // techs the notes say to avoid
  seniorRequired?: boolean; // needs an experienced/master hand
  // Display-only (carried through for the review UI):
  name?: string;
  city?: string | null;
  window?: string | null;
  lockReason?: string | null; // why it's anchored (PART/TECH/Install/Requested…)
  note?: string | null; // what the call is about (job preview) — for the hover
  asap?: boolean;
}

export interface OptiTech {
  empno: string;
  skills: Set<number>;
  senior: boolean;
}

export interface Reassignment {
  id: string;
  from: string;
  to: string;
  savedMiles: number;
}

export interface WholeDayPlan {
  /** proposed ordered stop-ids per tech (empno → ids in run order) */
  perTech: Record<string, string[]>;
  reassignments: Reassignment[];
  milesBefore: number;
  milesAfter: number;
  minutesSaved: number;
}

const sortByTime = (a: OptiStop, b: OptiStop) => a.t.localeCompare(b.t);

/** Miles for a set of stops in their current (planned-time) order — today's reality. */
function currentMiles(arr: OptiStop[]): number {
  return routeMiles([...arr].sort(sortByTime));
}

/** Miles for a set of stops in window-aware optimized order. */
function optimizedMiles(arr: OptiStop[]): number {
  return routeMiles(optimizeBanded(arr.map((s) => ({ lat: s.lat, lng: s.lng, band: s.band }))));
}

/**
 * Plan the whole day. Movable calls relocate to the cheapest qualified tech; every
 * tech's run is then resequenced. `minSaveMiles` is the churn guard — a relocate
 * must save at least this much (default 1 mi) so we don't shuffle work for noise.
 */
export function planWholeDay(
  inStops: OptiStop[],
  techs: OptiTech[],
  minSaveMiles = 1,
): WholeDayPlan {
  // Clone so we never mutate the caller's RouteStops.
  const stops: OptiStop[] = inStops.map((s) => ({ ...s }));
  const techByEmpno = new Map(techs.map((t) => [t.empno, t]));

  const byTech = new Map<string, OptiStop[]>();
  for (const s of stops) {
    if (!s.empno) continue;
    if (!byTech.has(s.empno)) byTech.set(s.empno, []);
    byTech.get(s.empno)!.push(s);
  }

  const milesBefore = [...byTech.values()].reduce((m, arr) => m + currentMiles(arr), 0);

  // Can tech `empno` take movable stop `s`? (skills, seniority, not-excluded)
  const qualifies = (s: OptiStop, empno: string): boolean => {
    if (empno === s.empno) return true;
    if (s.excludedEmpnos.includes(empno)) return false;
    const t = techByEmpno.get(empno);
    if (!t) return false;
    if (s.requiredSkills.some((sk) => !t.skills.has(sk))) return false;
    if (s.seniorRequired && !t.senior) return false;
    return true;
  };

  // Greedy relocate: keep moving the best-improving movable call until stable.
  const moves = new Map<string, Reassignment>(); // by stop id (latest wins)
  let improved = true;
  let rounds = 0;
  while (improved && rounds < 5) {
    improved = false;
    rounds++;
    for (const s of stops) {
      if (!s.movable) continue;
      const cur = byTech.get(s.empno) ?? [];
      // marginal cost of S on its current tech (optimized-order aware)
      const curMarg = optimizedMiles(cur) - optimizedMiles(cur.filter((x) => x.id !== s.id));
      let bestEmpno = s.empno;
      let bestSave = 0;
      for (const t of techs) {
        if (t.empno === s.empno || !qualifies(s, t.empno)) continue;
        const tgt = byTech.get(t.empno) ?? [];
        const addMarg = optimizedMiles([...tgt, s]) - optimizedMiles(tgt);
        const save = curMarg - addMarg;
        if (save > bestSave + 1e-9) {
          bestSave = save;
          bestEmpno = t.empno;
        }
      }
      if (bestEmpno !== s.empno && bestSave >= minSaveMiles) {
        // perform the relocate on the working copy
        byTech.set(
          s.empno,
          cur.filter((x) => x.id !== s.id),
        );
        const tgt = byTech.get(bestEmpno) ?? [];
        byTech.set(bestEmpno, [...tgt, s]);
        const origin = moves.get(s.id)?.from ?? s.empno;
        s.empno = bestEmpno;
        moves.set(s.id, { id: s.id, from: origin, to: bestEmpno, savedMiles: bestSave });
        improved = true;
      }
    }
  }

  // Final per-tech sequence + after-miles.
  const perTech: Record<string, string[]> = {};
  let milesAfter = 0;
  for (const [empno, arr] of byTech) {
    if (arr.length === 0) continue;
    const ordered = optimizeBanded(
      arr.map((s) => ({ lat: s.lat, lng: s.lng, band: s.band, id: s.id })),
    ) as (LatLng & { band: number; id: string })[];
    perTech[empno] = ordered.map((o) => o.id);
    milesAfter += routeMiles(ordered);
  }

  const reassignments = [...moves.values()].filter((r) => r.from !== r.to);
  return {
    perTech,
    reassignments,
    milesBefore,
    milesAfter,
    minutesSaved: driveMinutes(Math.max(0, milesBefore - milesAfter)),
  };
}
